import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { getApiUrl } from '../../core/http/http-client';

@Component({
  selector: 'app-social-callback',
  standalone: true,
  imports: [CommonModule, RouterLink, IconComponent],
  template: `
    <div class="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#09090b] dark:text-slate-100 flex flex-col items-center justify-center p-4 antialiased">
      <div class="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm text-center space-y-6">
        <img
          src="/icons/lom-192.png"
          alt="LOM"
          width="80"
          height="80"
          class="inline-block w-20 h-20 rounded-2xl object-contain shadow-md mx-auto"
        />

        @if (errorMessage()) {
          <div class="space-y-4">
            <div class="w-12 h-12 mx-auto rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center">
              <app-icon name="alert-circle" [size]="24" />
            </div>
            <h2 class="text-base font-bold text-slate-900 dark:text-white" role="status">
              {{ errorMessage() }}
            </h2>
            <div>
              <a
                routerLink="/login"
                class="inline-flex items-center justify-center space-x-2 py-2.5 px-4 rounded-xl text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition-all shadow-xs cursor-pointer"
              >
                <app-icon name="arrow-left" [size]="14" />
                <span>{{ game.t('auth.google_back_to_login') }}</span>
              </a>
            </div>
          </div>
        } @else {
          <div class="space-y-4 py-6">
            <app-icon name="loader" [size]="32" class="animate-spin mx-auto text-emerald-600 dark:text-emerald-400" />
            <h2 class="text-base font-bold text-slate-900 dark:text-white" role="status">
              {{ game.t('auth.google_checking') }}
            </h2>
            <p class="text-xs text-slate-400">Por favor, espera un momento.</p>
          </div>
        }
      </div>
    </div>
  `
})
export class SocialCallbackComponent implements OnInit {
  readonly game = inject(GameService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly errorMessage = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    const errorParam = this.route.snapshot.queryParamMap.get('error');
    if (errorParam) {
      let msgKey = 'auth.google_err_unavailable';
      if (errorParam === 'cancelled') {
        msgKey = 'auth.google_err_cancelled';
      } else if (errorParam === 'expired') {
        msgKey = 'auth.google_err_expired';
      } else if (errorParam === 'not_allowed') {
        msgKey = 'auth.google_err_not_allowed';
      }
      this.errorMessage.set(this.game.t(msgKey));
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(getApiUrl('/api/users/me'), {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      if (!data || !data.id || !data.email) {
        throw new Error('Invalid user profile response');
      }

      await this.game.handleSocialLoginSuccess({
        id: data.id,
        email: data.email,
        username: data.username,
      });

      const invitation = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('lom_pending_invitation') : null;
      if (invitation) {
        sessionStorage.removeItem('lom_pending_invitation');
        if (typeof history !== 'undefined') {
          history.replaceState(null, '', '/leagues');
        }
        await this.router.navigate(['/leagues'], { fragment: `invitation=${invitation}`, replaceUrl: true });
      } else {
        if (typeof history !== 'undefined') {
          history.replaceState(null, '', '/dashboard');
        }
        await this.router.navigate(['/dashboard'], { replaceUrl: true });
      }
    } catch (err: any) {
      console.error('[SocialCallback] Failed to verify user session:', err);
      this.errorMessage.set(this.game.t('auth.google_err_unavailable'));
    }
  }
}
